import React, { useState, useEffect, useCallback, useContext, useRef } from "react";
import {
  Drawer, IconButton, Typography, Divider,
  TextField, Select, MenuItem, FormControl, InputLabel,
  Button, CircularProgress, Tooltip, Dialog,
  DialogTitle, DialogContent, DialogActions, Tabs, Tab,
  Chip, FormControlLabel, Switch, Paper,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import CloseIcon from "@material-ui/icons/Close";
import EventIcon from "@material-ui/icons/Event";
import ChevronLeftIcon from "@material-ui/icons/ChevronLeft";
import ChevronRightIcon from "@material-ui/icons/ChevronRight";
import WarningIcon from "@material-ui/icons/Warning";
import { toast } from "react-toastify";
import {
  format, startOfMonth, endOfMonth, addMonths, subMonths,
  getDaysInMonth, getDay,
} from "date-fns";
import { es } from "date-fns/locale";
import { AuthContext } from "../../context/Auth/AuthContext";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { useHistory } from "react-router-dom";

// FIX 2: persistent drawer width 380px
const drawerWidth = 380;

const useStyles = makeStyles(theme => ({
  // FIX 2: drawer class makes this a flex child of the same width as the Paper
  drawer: {
    width: drawerWidth,
    flexShrink: 0,
  },
  drawerPaper: {
    width: drawerWidth,
    display: "flex",
    flexDirection: "column",
    borderTop: `1px solid ${theme.palette.divider}`,
    borderRight: `1px solid ${theme.palette.divider}`,
    borderBottom: `1px solid ${theme.palette.divider}`,
    borderTopRightRadius: 4,
    borderBottomRightRadius: 4,
    backgroundColor: theme.palette.background.paper,
    // Smooth transition when right offset changes (ContactDrawer opens/closes)
    transition: theme.transitions.create("right", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: theme.spacing(1, 2),
    borderBottom: `1px solid ${theme.palette.divider}`,
    minHeight: 56,
    flexShrink: 0,
  },
  content: {
    flex: 1,
    overflowY: "auto",
    padding: theme.spacing(2),
    ...theme.scrollbarStyles,
  },
  formField: {
    marginBottom: theme.spacing(2),
  },
  appointmentCard: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 8,
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
    backgroundColor: theme.palette.background.default,
  },
  appointmentRow: {
    display: "flex",
    justifyContent: "space-between",
    marginBottom: 6,
    fontSize: "0.875rem",
  },
  label: {
    fontWeight: 600,
    color: theme.palette.text.secondary,
    fontSize: "0.75rem",
    textTransform: "uppercase",
    marginBottom: 2,
  },
  calendarHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: theme.spacing(1),
  },
  calendarGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(7, 1fr)",
    gap: 2,
    marginBottom: theme.spacing(1),
  },
  dayHeader: {
    textAlign: "center",
    fontSize: "0.7rem",
    color: theme.palette.text.secondary,
    fontWeight: 600,
    padding: "4px 0",
  },
  dayCell: {
    textAlign: "center",
    padding: "6px 2px",
    borderRadius: 4,
    cursor: "pointer",
    position: "relative",
    fontSize: "0.8rem",
    "&:hover": {
      backgroundColor: theme.palette.action.hover,
    },
  },
  dayCellEmpty: {
    padding: "6px 2px",
  },
  dayCellHasAppt: {
    backgroundColor: theme.palette.primary.light + "22",
    fontWeight: 700,
    color: theme.palette.primary.main,
  },
  dayCellSelected: {
    backgroundColor: theme.palette.primary.main,
    color: "#fff",
    fontWeight: 700,
  },
  apptDot: {
    width: 5,
    height: 5,
    borderRadius: "50%",
    backgroundColor: theme.palette.primary.main,
    margin: "0 auto",
    marginTop: 2,
  },
  apptListItem: {
    padding: "8px 10px",
    borderRadius: 6,
    border: `1px solid ${theme.palette.divider}`,
    marginBottom: 6,
    cursor: "pointer",
    fontSize: "0.85rem",
    "&:hover": {
      backgroundColor: theme.palette.action.hover,
    },
  },
  previewBox: {
    backgroundColor: theme.palette.background.default,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 6,
    padding: theme.spacing(1.5),
    marginBottom: theme.spacing(2),
    fontSize: "0.8rem",
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
    color: theme.palette.text.secondary,
  },
  notifSwitchRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: 4,
  },
}));

const DAYS_OF_WEEK = ["Dom", "Lun", "Mar", "Mié", "Jue", "Vie", "Sáb"];

// Helper: replace message template variables for live preview
const previewMessage = (template, { contactName, scheduledAt, detail, notes, companyName, notif3Minutes }) => {
  if (!template) return "";
  let text = template
    .replace(/\{\{nombre\}\}/g, contactName || "Cliente")
    .replace(/\{\{fecha\}\}/g, scheduledAt ? format(new Date(scheduledAt), "dd/MM/yyyy") : "DD/MM/YYYY")
    .replace(/\{\{hora\}\}/g, scheduledAt ? format(new Date(scheduledAt), "HH:mm") : "HH:MM")
    .replace(/\{\{empresa\}\}/g, companyName || "")
    .replace(/\{\{detalle\}\}/g, detail || "")
    .replace(/\{\{minutos\}\}/g, String(notif3Minutes || 60));
  if (notes) text += `\n> ${notes}`;
  return text;
};

// FIX 2: accepts rightOffset prop for persistent dual-drawer stacking
const AppointmentDrawer = ({ open, onClose, ticket, contact, rightOffset = 0 }) => {
  const classes = useStyles();
  const { user } = useContext(AuthContext);
  const history = useHistory();

  const [tab, setTab] = useState(0);
  const [appointment, setAppointment] = useState(null);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(false);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);

  // Form state
  const [scheduledAt, setScheduledAt] = useState("");
  const [detail, setDetail] = useState("");
  const [detailCustom, setDetailCustom] = useState("");
  const [notes, setNotes] = useState("");
  const [staffPhone, setStaffPhone] = useState("");
  const [notifyStaff, setNotifyStaff] = useState(false);
  const [whatsappId, setWhatsappId] = useState("");
  // FIX 4: per-appointment notif switches
  const [notif1Enabled, setNotif1Enabled] = useState(true);
  const [notif2Enabled, setNotif2Enabled] = useState(true);
  const [notif3Enabled, setNotif3Enabled] = useState(true);

  // Settings
  const [detailOptions, setDetailOptions] = useState([]);
  const [wbots, setWbots] = useState([]);
  const [defaultWhatsappId, setDefaultWhatsappId] = useState("");
  // FIX 4: notif text templates from settings
  const [notif1Text, setNotif1Text] = useState(
    "Hola {{nombre}}, te recordamos que mañana tenés una cita de *{{detalle}}* a las {{hora}} hs. — {{empresa}}"
  );
  const [notif2Text, setNotif2Text] = useState(
    "Hola {{nombre}}, hoy es el día de tu cita de *{{detalle}}* a las {{hora}} hs. ¡Te esperamos! — {{empresa}}"
  );
  const [notif3Text, setNotif3Text] = useState(
    "Hola {{nombre}}, en {{minutos}} minutos tenés tu cita de *{{detalle}}* ({{hora}} hs). — {{empresa}}"
  );
  const [notif3Minutes, setNotif3Minutes] = useState(60);
  const [notif1GlobalEnabled, setNotif1GlobalEnabled] = useState(true);
  const [notif2GlobalEnabled, setNotif2GlobalEnabled] = useState(true);
  const [notif3GlobalEnabled, setNotif3GlobalEnabled] = useState(true);

  // Calendar
  const [calMonth, setCalMonth] = useState(new Date());
  const [calAppointments, setCalAppointments] = useState([]);
  const [calLoading, setCalLoading] = useState(false);
  const [selectedDay, setSelectedDay] = useState(null);

  const isAdmin = user.profile === "admin" || user.isSuperAdmin;

  // FIX 3: ref for datetime input — used to focus after "Agendar este día"
  const dateTimeInputRef = useRef(null);

  // Fetch appointment for this ticket
  const fetchAppointment = useCallback(async () => {
    if (!ticket?.id) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/appointments/ticket/${ticket.id}`);
      setAppointment(data);
      setScheduledAt(data.scheduledAt ? data.scheduledAt.slice(0, 16) : "");
      setDetail(data.detail || "");
      setDetailCustom("");
      setNotes(data.notes || "");
      setStaffPhone(data.staffPhone || "");
      setNotifyStaff(!!(data.staffPhone));
      setWhatsappId(data.whatsappId ? String(data.whatsappId) : "");
      // FIX 4: load per-appointment notif switches
      setNotif1Enabled(data.notif1_enabled !== false);
      setNotif2Enabled(data.notif2_enabled !== false);
      setNotif3Enabled(data.notif3_enabled !== false);
    } catch (err) {
      if (err?.response?.status === 404) {
        setAppointment(null);
        setScheduledAt("");
        setDetail("");
        setDetailCustom("");
        setNotes("");
        // FIX 5: pre-populate staffPhone from contact.number when creating new
        setStaffPhone(contact?.number || "");
        setNotifyStaff(!!(contact?.number));
        setWhatsappId(defaultWhatsappId);
        setNotif1Enabled(true);
        setNotif2Enabled(true);
        setNotif3Enabled(true);
      } else {
        toastError(err);
      }
    } finally {
      setLoading(false);
    }
  }, [ticket?.id, defaultWhatsappId, contact?.number]);

  // Fetch settings
  const fetchSettings = useCallback(async () => {
    try {
      const { data } = await api.get("/settings");
      const settingsMap = {};
      (data?.settings || data || []).forEach(s => { settingsMap[s.key] = s.value; });

      const rawOptions = settingsMap["appointmentDetailOptions"];
      if (rawOptions) {
        try { setDetailOptions(JSON.parse(rawOptions)); } catch (_) {}
      }
      const defWa = settingsMap["appointmentsDefaultWhatsappId"] || "";
      setDefaultWhatsappId(defWa);
      setWhatsappId(prev => prev || defWa);

      // FIX 4: load notif text templates
      if (settingsMap["appointmentsNotif1Text"]) setNotif1Text(settingsMap["appointmentsNotif1Text"]);
      if (settingsMap["appointmentsNotif2Text"]) setNotif2Text(settingsMap["appointmentsNotif2Text"]);
      if (settingsMap["appointmentsNotif3Text"]) setNotif3Text(settingsMap["appointmentsNotif3Text"]);
      if (settingsMap["appointmentsNotif3MinutesBefore"]) setNotif3Minutes(Number(settingsMap["appointmentsNotif3MinutesBefore"]));
      setNotif1GlobalEnabled(settingsMap["appointmentsNotif1Enabled"] !== "false");
      setNotif2GlobalEnabled(settingsMap["appointmentsNotif2Enabled"] !== "false");
      setNotif3GlobalEnabled(settingsMap["appointmentsNotif3Enabled"] !== "false");
    } catch (_) {}
  }, []);

  // Fetch connected wbots
  const fetchWbots = useCallback(async () => {
    try {
      const { data } = await api.get("/whatsapp");
      setWbots((data || []).filter(w => w.status === "CONNECTED"));
    } catch (_) {}
  }, []);

  useEffect(() => {
    if (open) {
      fetchSettings();
      fetchWbots();
      fetchAppointment();
    }
  }, [open, fetchAppointment, fetchSettings, fetchWbots]);

  // Fetch calendar appointments when month changes or tab=1
  const fetchCalendar = useCallback(async () => {
    setCalLoading(true);
    try {
      const start = startOfMonth(calMonth);
      const end = endOfMonth(calMonth);
      const { data } = await api.get("/appointments", {
        params: { startDate: start.toISOString(), endDate: end.toISOString() }
      });
      setCalAppointments(data || []);
    } catch (err) {
      toastError(err);
    } finally {
      setCalLoading(false);
    }
  }, [calMonth]);

  useEffect(() => {
    if (open && tab === 1) {
      fetchCalendar();
    }
  }, [open, tab, fetchCalendar]);

  // FIX 2: reset day filter when drawer opens or when switching to calendar tab
  useEffect(() => {
    if (open) setSelectedDay(null);
  }, [open]);

  useEffect(() => {
    if (tab === 1) setSelectedDay(null);
  }, [tab]);

  const handleSave = async () => {
    const finalDetail = detail === "__other__" ? detailCustom : detail;
    const finalWhatsappId = whatsappId || undefined;

    try {
      const finalStaffPhone = notifyStaff ? staffPhone : "";
      if (appointment) {
        await api.put(`/appointments/${appointment.id}`, {
          scheduledAt,
          detail: finalDetail,
          notes,
          staffPhone: finalStaffPhone,
          whatsappId: finalWhatsappId || null,
          notif1_enabled: notif1Enabled,
          notif2_enabled: notif2Enabled,
          notif3_enabled: notif3Enabled,
        });
        toast.success("Cita actualizada");
      } else {
        await api.post("/appointments", {
          ticketId: ticket?.id,
          contactId: contact?.id || ticket?.contact?.id,
          scheduledAt,
          detail: finalDetail,
          notes,
          staffPhone: finalStaffPhone,
          whatsappId: finalWhatsappId || null,
          notif1_enabled: notif1Enabled,
          notif2_enabled: notif2Enabled,
          notif3_enabled: notif3Enabled,
        });
        toast.success("Cita agendada");
      }
      setEditing(false);
      fetchAppointment();
      // FIX 1: keep calendar in sync after create/update
      fetchCalendar();
    } catch (err) {
      toastError(err);
    }
  };

  const handleCancel = async () => {
    if (!appointment) return;
    try {
      await api.delete(`/appointments/${appointment.id}`);
      toast.success("Cita cancelada");
      setAppointment(null);
      setCancelDialogOpen(false);
      // FIX 1: sync calendar after cancel
      fetchCalendar();
    } catch (err) {
      toastError(err);
    }
  };

  // Calendar helpers
  const daysInMonth = getDaysInMonth(calMonth);
  const firstDayOfWeek = getDay(startOfMonth(calMonth));

  const apptsByDay = {};
  calAppointments.forEach(apt => {
    const d = new Date(apt.scheduledAt).getDate();
    if (!apptsByDay[d]) apptsByDay[d] = [];
    apptsByDay[d].push(apt);
  });

  // FIX 4: build live preview vars
  const previewVars = {
    contactName: contact?.name,
    scheduledAt: scheduledAt ? scheduledAt.replace("T", " ") : null,
    detail: detail === "__other__" ? detailCustom : detail,
    notes,
    companyName: user?.company?.name,
    notif3Minutes,
  };

  const renderForm = () => (
    <div>
      {/* FIX 5: warning if contact has no phone number */}
      {!contact?.number && (
        <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 12,
          padding: "6px 10px", borderRadius: 6, backgroundColor: "#fff3cd", color: "#856404", fontSize: "0.8rem" }}>
          <WarningIcon style={{ fontSize: 16 }} />
          El contacto no tiene número de teléfono registrado
        </div>
      )}

      {/* Tipo de cita */}
      <FormControl fullWidth className={classes.formField} variant="outlined" size="small">
        <InputLabel>Tipo de cita</InputLabel>
        <Select
          value={detail}
          onChange={e => setDetail(e.target.value)}
          label="Tipo de cita"
        >
          {detailOptions.map(opt => (
            <MenuItem key={opt} value={opt}>{opt}</MenuItem>
          ))}
          <MenuItem value="__other__">Otro...</MenuItem>
        </Select>
      </FormControl>

      {detail === "__other__" && (
        <TextField
          fullWidth
          className={classes.formField}
          label="Especificar tipo"
          variant="outlined"
          size="small"
          value={detailCustom}
          onChange={e => setDetailCustom(e.target.value)}
        />
      )}

      {/* Fecha y hora */}
      <TextField
        fullWidth
        className={classes.formField}
        label="Fecha y hora"
        type="datetime-local"
        variant="outlined"
        size="small"
        InputLabelProps={{ shrink: true }}
        inputRef={dateTimeInputRef}
        value={scheduledAt}
        onChange={e => setScheduledAt(e.target.value)}
      />

      {/* Notas */}
      <TextField
        fullWidth
        className={classes.formField}
        label="Notas adicionales"
        variant="outlined"
        size="small"
        multiline
        rows={2}
        value={notes}
        onChange={e => setNotes(e.target.value)}
        helperText="Se adjuntan al final de cada aviso enviado"
      />

      {/* Staff phone — con toggle */}
      <div style={{ marginBottom: 12, padding: "8px 12px", border: "1px solid rgba(0,0,0,0.12)", borderRadius: 8 }}>
        <FormControlLabel
          style={{ margin: 0 }}
          control={
            <Switch
              size="small"
              color="primary"
              checked={notifyStaff}
              onChange={e => setNotifyStaff(e.target.checked)}
            />
          }
          label={<Typography variant="body2" style={{ fontWeight: 500 }}>Avisar al personal</Typography>}
        />
        {notifyStaff && (
          <TextField
            fullWidth
            style={{ marginTop: 8 }}
            label="Número del personal"
            variant="outlined"
            size="small"
            placeholder="5493511234567"
            value={staffPhone}
            onChange={e => setStaffPhone(e.target.value)}
          />
        )}
      </div>

      {/* Canal WA — solo si hay más de 1 */}
      {wbots.length > 1 && (
        <FormControl fullWidth className={classes.formField} variant="outlined" size="small">
          <InputLabel>Canal WhatsApp</InputLabel>
          <Select
            value={whatsappId}
            onChange={e => setWhatsappId(e.target.value)}
            label="Canal WhatsApp"
          >
            <MenuItem value="">Predeterminado</MenuItem>
            {wbots.map(w => (
              <MenuItem key={w.id} value={String(w.id)}>{w.name}</MenuItem>
            ))}
          </Select>
        </FormControl>
      )}

      {/* FIX 4: per-appointment notif switches */}
      <div style={{ marginBottom: 12 }}>
        <Typography variant="caption" style={{ fontWeight: 600, textTransform: "uppercase",
          color: "rgba(0,0,0,0.54)", display: "block", marginBottom: 4 }}>
          Avisos a enviar
        </Typography>
        {[
          { label: "Aviso 1 — 24h antes", enabled: notif1Enabled, setEnabled: setNotif1Enabled, globalOk: notif1GlobalEnabled },
          { label: "Aviso 2 — día de la cita", enabled: notif2Enabled, setEnabled: setNotif2Enabled, globalOk: notif2GlobalEnabled },
          { label: `Aviso 3 — ${notif3Minutes} min antes`, enabled: notif3Enabled, setEnabled: setNotif3Enabled, globalOk: notif3GlobalEnabled },
        ].map(({ label, enabled, setEnabled, globalOk }) => (
          <div key={label} className={classes.notifSwitchRow}>
            <Typography variant="body2" style={{ color: globalOk ? "inherit" : "rgba(0,0,0,0.38)" }}>
              {label}{!globalOk && " (desactivado globalmente)"}
            </Typography>
            <Switch
              size="small"
              checked={enabled && globalOk}
              onChange={e => setEnabled(e.target.checked)}
              disabled={!globalOk}
              color="primary"
            />
          </div>
        ))}
      </div>

      {/* FIX 4: live message preview */}
      {scheduledAt && (
        <div style={{ marginBottom: 12 }}>
          <Typography variant="caption" style={{ fontWeight: 600, textTransform: "uppercase",
            color: "rgba(0,0,0,0.54)", display: "block", marginBottom: 4 }}>
            Vista previa de mensajes
          </Typography>
          {[
            { label: "Aviso 1", text: notif1Text, enabled: notif1Enabled && notif1GlobalEnabled },
            { label: "Aviso 2", text: notif2Text, enabled: notif2Enabled && notif2GlobalEnabled },
            { label: "Aviso 3", text: notif3Text, enabled: notif3Enabled && notif3GlobalEnabled },
          ].filter(n => n.enabled).map(({ label, text }) => (
            <div key={label} style={{ marginBottom: 8 }}>
              <Typography variant="caption" style={{ color: "rgba(0,0,0,0.54)", display: "block" }}>
                {label}:
              </Typography>
              <div className={classes.previewBox}>
                {previewMessage(text, previewVars)}
              </div>
            </div>
          ))}
          {![notif1Enabled && notif1GlobalEnabled, notif2Enabled && notif2GlobalEnabled, notif3Enabled && notif3GlobalEnabled].some(Boolean) && (
            <Typography variant="caption" color="textSecondary">
              Todos los avisos desactivados — no se enviará ningún recordatorio.
            </Typography>
          )}
        </div>
      )}

      <Button
        variant="contained"
        color="primary"
        fullWidth
        onClick={handleSave}
        disabled={!scheduledAt}
      >
        {appointment ? "Guardar cambios" : "Agendar cita"}
      </Button>
    </div>
  );

  const renderAppointmentView = () => {
    if (!appointment) return renderForm();

    if (editing) {
      return (
        <div>
          <Button size="small" onClick={() => setEditing(false)} style={{ marginBottom: 12 }}>
            ← Volver
          </Button>
          {renderForm()}
        </div>
      );
    }

    return (
      <div className={classes.appointmentCard}>
        <div className={classes.appointmentRow}>
          <span className={classes.label}>Tipo</span>
          <span>{appointment.detail || "—"}</span>
        </div>
        <div className={classes.appointmentRow}>
          <span className={classes.label}>Fecha</span>
          <span>{format(new Date(appointment.scheduledAt), "dd/MM/yyyy HH:mm")}</span>
        </div>
        {appointment.notes && (
          <div className={classes.appointmentRow}>
            <span className={classes.label}>Notas</span>
            <span style={{ textAlign: "right", maxWidth: "60%" }}>{appointment.notes}</span>
          </div>
        )}
        {appointment.staffPhone && (
          <div className={classes.appointmentRow}>
            <span className={classes.label}>Personal</span>
            <span>{appointment.staffPhone}</span>
          </div>
        )}
        {appointment.whatsapp && (
          <div className={classes.appointmentRow}>
            <span className={classes.label}>Canal</span>
            <span>{appointment.whatsapp.name}</span>
          </div>
        )}
        {appointment.createdBy && (
          <div className={classes.appointmentRow}>
            <span className={classes.label}>Creada por</span>
            <span>{appointment.createdBy.name}</span>
          </div>
        )}

        {/* Notif status summary */}
        <div style={{ display: "flex", gap: 4, flexWrap: "wrap", marginTop: 8, marginBottom: 12 }}>
          {[
            { label: "A1", sent: appointment.notif1_sent_at, skipped: appointment.notif1_skipped, failed: appointment.notif1_failed, enabled: appointment.notif1_enabled !== false },
            { label: "A2", sent: appointment.notif2_sent_at, skipped: appointment.notif2_skipped, failed: appointment.notif2_failed, enabled: appointment.notif2_enabled !== false },
            { label: "A3", sent: appointment.notif3_sent_at, skipped: appointment.notif3_skipped, failed: appointment.notif3_failed, enabled: appointment.notif3_enabled !== false },
          ].map(({ label, sent, skipped, failed, enabled }) => {
            let chipLabel = label;
            let chipStyle = { fontSize: 10, height: 20 };
            if (!enabled) { chipLabel += " ✗"; chipStyle = { ...chipStyle, opacity: 0.4 }; }
            else if (sent) { chipLabel += " ✓"; chipStyle = { ...chipStyle, backgroundColor: "#10b981", color: "#fff" }; }
            else if (failed) { chipLabel += " !"; chipStyle = { ...chipStyle, backgroundColor: "#ef4444", color: "#fff" }; }
            else if (skipped) { chipLabel += " —"; chipStyle = { ...chipStyle, backgroundColor: "#9ca3af", color: "#fff" }; }
            else { chipStyle = { ...chipStyle, backgroundColor: "#fbbf24", color: "#fff" }; }
            return <Chip key={label} label={chipLabel} size="small" style={chipStyle} />;
          })}
        </div>

        <div style={{ display: "flex", gap: 8, marginTop: 4 }}>
          <Button size="small" variant="outlined" onClick={() => setEditing(true)}>
            Editar
          </Button>
          <Button
            size="small"
            variant="outlined"
            style={{ color: "#ef4444", borderColor: "#ef4444" }}
            onClick={() => setCancelDialogOpen(true)}
          >
            Cancelar cita
          </Button>
        </div>
      </div>
    );
  };

  // FIX 3: switch to "Esta Cita" tab with date pre-filled and input focused
  const handleAgendarEsteDia = (day) => {
    const year = calMonth.getFullYear();
    const month = String(calMonth.getMonth() + 1).padStart(2, "0");
    const dayStr = String(day).padStart(2, "0");
    setScheduledAt(`${year}-${month}-${dayStr}T09:00`);
    setTab(0);
    setTimeout(() => {
      if (dateTimeInputRef.current) dateTimeInputRef.current.focus();
    }, 150);
  };

  // FIX 3: calendar with always-visible chronological list + click-day filter
  const renderCalendar = () => {
    const cells = [];

    for (let i = 0; i < firstDayOfWeek; i++) {
      cells.push(<div key={`empty-${i}`} className={classes.dayCellEmpty} />);
    }

    for (let d = 1; d <= daysInMonth; d++) {
      const hasAppt = !!apptsByDay[d];
      const isSelected = selectedDay === d;
      const cellClass = [
        classes.dayCell,
        hasAppt && classes.dayCellHasAppt,
        isSelected && classes.dayCellSelected,
      ].filter(Boolean).join(" ");

      cells.push(
        <div
          key={d}
          className={cellClass}
          onClick={() => setSelectedDay(isSelected ? null : d)}
        >
          {d}
          {hasAppt && !isSelected && <div className={classes.apptDot} />}
        </div>
      );
    }

    // FIX 3: always-visible chronological list; filtered when a day is selected
    const sortedAll = [...calAppointments].sort(
      (a, b) => new Date(a.scheduledAt) - new Date(b.scheduledAt)
    );
    const displayedAppts = selectedDay
      ? (apptsByDay[selectedDay] || []).sort((a, b) => new Date(a.scheduledAt) - new Date(b.scheduledAt))
      : sortedAll;

    return (
      <div>
        {/* Calendar nav */}
        <div className={classes.calendarHeader}>
          <IconButton size="small" onClick={() => { setCalMonth(subMonths(calMonth, 1)); setSelectedDay(null); }}>
            <ChevronLeftIcon />
          </IconButton>
          <Typography variant="subtitle2">
            {format(calMonth, "MMMM yyyy", { locale: es })}
          </Typography>
          <IconButton size="small" onClick={() => { setCalMonth(addMonths(calMonth, 1)); setSelectedDay(null); }}>
            <ChevronRightIcon />
          </IconButton>
        </div>

        {calLoading ? (
          <div style={{ textAlign: "center", padding: 16 }}>
            <CircularProgress size={24} />
          </div>
        ) : (
          <>
            <div className={classes.calendarGrid}>
              {DAYS_OF_WEEK.map(d => (
                <div key={d} className={classes.dayHeader}>{d}</div>
              ))}
              {cells}
            </div>

            {/* FIX 3: filter chip + Agendar button when a day is selected */}
            {selectedDay && (
              <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap", marginBottom: 8, marginTop: 4 }}>
                <Chip
                  label={`Citas del ${selectedDay}/${calMonth.getMonth() + 1}`}
                  onDelete={() => setSelectedDay(null)}
                  size="small"
                  color="primary"
                />
                {/* Secondary "Agendar" when day has appointments */}
                {(apptsByDay[selectedDay] || []).length > 0 && (
                  <Button
                    size="small"
                    variant="outlined"
                    color="primary"
                    style={{ fontSize: "0.75rem", padding: "2px 8px" }}
                    onClick={() => handleAgendarEsteDia(selectedDay)}
                  >
                    ＋ Agendar este día
                  </Button>
                )}
              </div>
            )}

            {/* FIX 3: always-visible list */}
            {displayedAppts.length > 0 ? (
              <div>
                {!selectedDay && (
                  <Typography variant="caption" style={{ fontWeight: 600, display: "block", marginBottom: 6, color: "rgba(0,0,0,0.54)" }}>
                    {calAppointments.length} cita{calAppointments.length !== 1 ? "s" : ""} este mes
                  </Typography>
                )}
                {displayedAppts.map(apt => (
                  <div
                    key={apt.id}
                    className={classes.apptListItem}
                    onClick={() => apt.ticketId && history.push(`/tickets/${apt.ticketId}`)}
                  >
                    <div style={{ fontWeight: 600, fontSize: "0.8rem", color: "rgba(0,0,0,0.54)" }}>
                      {format(new Date(apt.scheduledAt), "dd/MM HH:mm")}
                    </div>
                    <div>
                      {apt.contact?.name || "Sin nombre"}
                      {apt.detail ? ` — ${apt.detail}` : ""}
                    </div>
                  </div>
                ))}
              </div>
            ) : selectedDay ? (
              /* FIX 3: prominent "Agendar" when day has NO appointments */
              <div style={{ textAlign: "center", padding: "8px 0" }}>
                <Typography variant="caption" color="textSecondary" style={{ display: "block", marginBottom: 10 }}>
                  Sin citas agendadas
                </Typography>
                <Button
                  variant="contained"
                  color="primary"
                  size="small"
                  fullWidth
                  onClick={() => handleAgendarEsteDia(selectedDay)}
                >
                  ＋ Agendar este día →
                </Button>
              </div>
            ) : (
              <Typography variant="caption" color="textSecondary">
                Sin citas este mes.
              </Typography>
            )}
          </>
        )}
      </div>
    );
  };

  return (
    <>
      {/* FIX 2: variant="persistent" with className for flex width + PaperProps for absolute positioning */}
      <Drawer
        className={classes.drawer}
        variant="persistent"
        anchor="right"
        open={open}
        classes={{ paper: classes.drawerPaper }}
        PaperProps={{ style: { position: "absolute", right: rightOffset } }}
      >
        {/* Header */}
        <div className={classes.header}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <EventIcon color="primary" fontSize="small" />
            <Typography variant="subtitle1" style={{ fontWeight: 600 }}>
              Citas
            </Typography>
          </div>
          <IconButton size="small" onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </div>

        {/* Tabs */}
        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          indicatorColor="primary"
          textColor="primary"
          variant="fullWidth"
        >
          <Tab label="Esta cita" />
          <Tab label="Calendario" />
        </Tabs>

        <div className={classes.content}>
          {tab === 0 && (
            loading ? (
              <div style={{ textAlign: "center", padding: 24 }}>
                <CircularProgress size={28} />
              </div>
            ) : (
              <>
                {!appointment && (
                  <Typography variant="body2" color="textSecondary" style={{ marginBottom: 16 }}>
                    No hay cita agendada para este ticket. Completá el formulario para agendar una.
                  </Typography>
                )}
                {renderAppointmentView()}
              </>
            )
          )}

          {tab === 1 && renderCalendar()}
        </div>
      </Drawer>

      {/* Cancel confirm dialog */}
      <Dialog open={cancelDialogOpen} onClose={() => setCancelDialogOpen(false)}>
        <DialogTitle>Cancelar cita</DialogTitle>
        <DialogContent>
          <Typography>¿Confirmás la cancelación de esta cita?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCancelDialogOpen(false)}>No, volver</Button>
          <Button onClick={handleCancel} style={{ color: "#ef4444" }}>
            Sí, cancelar cita
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default AppointmentDrawer;
